import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import { Select, Checkbox, FieldTable, Input } from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldText,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import {
  getCarePlanDetails,
  getClientCarePlansPromise,
  getDiagnosisDetails,
  getMedications,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function KYPCCPCPDischargeSummaryv2() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [carePlans, setCarePlans] = useState([]);
  const [diagnosisDetails, setDiagnosisDetails] = useState([]);

  const agencyOptions = [
    { _id: "DCBS", name: "DCBS" },
    { _id: "Foster Care Agency", name: "Foster Care Agency" },
    { _id: "Mental Health Facility", name: "Mental Health Facility" },
    { _id: "Foster Parents", name: "Foster Parents" },
    { _id: "Biological Parents", name: "Biological Parents" },
    { _id: "School", name: "School" },
    { _id: "Therapeutic Services", name: "Therapeutic Services" },
    { _id: "Other", name: "Other" },
  ];

  useEffect(() => {
    if (Object.keys(data).length === 0) {
      return;
    }

    if (!submissionid && !loading && !loaded) {
      const loadRecords = async () => {
        setLoading(true);
        const results = await Promise.all([
          getMedications(clientid),
          getClientCarePlansPromise(clientid),
        ]);

        if (!isMountedRef.current || results?.length !== 2) {
          return;
        }

        const allMedications = results[0];
        setCarePlans(results[1]);

        if (allMedications?.length > 0) {
          const expiredMedications = allMedications.filter(
            (med) =>
              med.ccx_stopdate !== undefined &&
              moment(getFieldDate(med.ccx_stopdate)).isBefore(moment())
          );
          const currentMedications = allMedications.filter(
            (med) =>
              med.ccx_stopdate === undefined ||
              moment(getFieldDate(med.ccx_stopdate)).isSameOrAfter(moment())
          );

          const obj = { ...data };

          obj.PastMedications["@value"] = expiredMedications
            .map((med) => `${med.ccx_name}`)
            .join(", ");

          obj.CurrentMedications["@value"] = currentMedications
            .map((med) => `${med.ccx_name}`)
            .join(", ");

          setData(obj);
        }

        setLoading(false);
        setLoaded(true);
      };

      loadRecords();
    }

    if (!data.CaseNumber["@value"] && clientData?.client?.mpi) {
      const obj = { ...data };
      obj.CaseNumber["@value"] = clientData.client.mpi;
      setData(obj);
    }
  }, [data, clientData, clientid, loading, loaded, submissionid]);

  const handleSelectedProgramChange = async (e) => {
    const obj = handleChange(e);
    const input = e.currentTarget;
    if (!clientData?.programs) {
      return;
    }

    const selectedProgram = clientData.programs.filter(
      (option) => option.ccx_client_programid === input.value
    )[0];

    if (!selectedProgram) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = selectedProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(selectedProgram.ccx_programunit);
    setFormsyDetails(details);

    if (submissionid) {
      return;
    }

    const loadCarePlanGoals = async (selectedProgram) => {
      let goals = "";
      const carePlansBySelectedProgram = carePlans.filter(
        (carePlan) =>
          getFieldText(carePlan.ccx_clientprogram) === selectedProgram
      );

      const selectedCarePlan = carePlansBySelectedProgram.sort((a, b) => {
        const aDate = moment(getFieldDate(a.ccx_effectivedate));
        const bDate = moment(getFieldDate(b.ccx_effectivedate));

        return aDate.isAfter(bDate) ? 1 : aDate.isBefore(bDate) ? -1 : 0;
      })[0];

      if (selectedCarePlan !== undefined) {
        const carePlan = await getCarePlanDetails(
          selectedCarePlan.ccx_careplanid
        );

        goals = carePlan.plan_goals.map((goal) => goal.ccx_Description).join(`
`);
      }

      return goals;
    };

    const results = await Promise.all([
      getDiagnosisDetails(input.value),
      loadCarePlanGoals(input.value),
    ]);

    if (!isMountedRef.current || results?.length !== 2) {
      return;
    }

    setDiagnosisDetails(results[0]);
    const goals = results[1];

    const programAdmitDate = getFieldDate(
      clientData.programs.filter(
        (program) =>
          program.ccx_client_programid === data.SelectedProgram["@value"]
      )[0].ccx_admitdate
    );

    obj.AdmissionDate["@value"] = moment(programAdmitDate).format(
      moment.HTML5_FMT.DATE
    );
    obj.Goals["@value"] = goals;

    setData(obj);
  };

  const handleDischargeDateChange = (e) => {
    const obj = handleChange(e);
    const input = e.currentTarget;
    const dischargeDate = moment(input.value);

    const diagnoses = diagnosisDetails
      .filter((dx) => {
        const dxEffectiveDate = moment(getFieldDate(dx.ccx_effdate));
        const dxExpirationDate =
          dx.ccx_expdate !== undefined
            ? moment(getFieldDate(dx.ccx_expdate))
            : undefined;

        return (
          dxEffectiveDate.isSameOrBefore(dischargeDate) &&
          (!dxExpirationDate || dxExpirationDate.isSameOrAfter(dischargeDate))
        );
      })
      .map(
        (diagnosis) =>
          `${diagnosis["dx.ccx_code"]} - ${diagnosis["dx.ccx_name"]}`
      ).join(`
`);

    obj.Diagnosis["@value"] = diagnoses;
    setData(obj);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) => comment.field._text === "Entire Form----"
        ).length > 0 ? (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        ) : null}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 ? (
            <>
              <p style={{ textIndent: "40px" }}>
                The discharge summary provides an overview of the history of
                treatment and functioning while in an out-of-home care setting.
                Each of the following eight (8) content areas, including the
                bulleted items, should be addressed in the specified list or
                narrative format. The Agency has the option of using this
                discharge summary as a form and entering the information in the
                text boxes or as an outline of content to be covered in the
                Agency's preferred format. The discharge summary becomes part of
                the child's record and should be sent to the child's DCBS social
                service worker on the date of discharge or the following
                business day in the event of an unplanned discharge.
              </p>
              <div className="h5">Identifying Data</div>
              <div className="container">
                <Input
                  name="CaseNumber"
                  label="Case or client number:"
                  error={errors.CaseNumber}
                  value={data.CaseNumber["@value"]}
                  onChange={handleChange}
                  identical={compare("CaseNumber")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="GradeLevel"
                  label="Grade level:"
                  error={errors.GradeLevel}
                  value={data.GradeLevel["@value"]}
                  onChange={handleChange}
                  identical={compare("GradeLevel")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="AdmissionDate"
                  label="Date of admission:"
                  error={errors.AdmissionDate}
                  value={data.AdmissionDate["@value"]}
                  onChange={handleChange}
                  identical={compare("AdmissionDate")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  type="date"
                />
                <Input
                  name="DischargeDate"
                  label="Date of discharge:"
                  error={errors.DischargeDate}
                  value={data.DischargeDate["@value"]}
                  onChange={handleDischargeDateChange}
                  identical={compare("DischargeDate")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  type="date"
                />
                <Input
                  name="AdmissionLevelOfCare"
                  label="CRP level of care upon admission to current placement:"
                  error={errors.AdmissionLevelOfCare}
                  value={data.AdmissionLevelOfCare["@value"]}
                  onChange={handleChange}
                  identical={compare("AdmissionLevelOfCare")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Background Data</div>
              <div className="container">
                <Input
                  name="ReasonForAdmission"
                  label="Reason for Admission:"
                  error={errors.ReasonForAdmission}
                  value={data.ReasonForAdmission["@value"]}
                  onChange={handleChange}
                  identical={compare("ReasonForAdmission")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="PastPlacements"
                  label="Past Placements:"
                  error={errors.PastPlacements}
                  value={data.PastPlacements["@value"]}
                  onChange={handleChange}
                  identical={compare("PastPlacements")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="PlacementDisruptions"
                  label="Placement Disruptions, if applicable, to include reasons for disruptions:"
                  error={errors.PlacementDisruptions}
                  value={data.PlacementDisruptions["@value"]}
                  onChange={handleChange}
                  identical={compare("PlacementDisruptions")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="CurrentLivingSituation"
                  label="Current living situation, including location, with whom and length of time the child has lived at current placement:"
                  error={errors.CurrentLivingSituation}
                  value={data.CurrentLivingSituation["@value"]}
                  onChange={handleChange}
                  identical={compare("CurrentLivingSituation")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="TypeOfAttachment"
                  label="Assessment of the level and type of attachment with current placement:"
                  error={errors.TypeOfAttachment}
                  value={data.TypeOfAttachment["@value"]}
                  onChange={handleChange}
                  identical={compare("TypeOfAttachment")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Siblings"
                  label="Siblings, including number, gender, age, where each sibling lives, and if not with the child, then location and frequency of visitation:"
                  error={errors.Siblings}
                  value={data.Siblings["@value"]}
                  onChange={handleChange}
                  identical={compare("Siblings")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="HistoryOfAbuse"
                  label="History of abuse and/or neglect, including if the child was a victim or perpetrator:"
                  error={errors.HistoryOfAbuse}
                  value={data.HistoryOfAbuse["@value"]}
                  onChange={handleChange}
                  identical={compare("HistoryOfAbuse")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="PerpetratorName"
                  label="If the child was a victim, name the perpetrator"
                  error={errors.PerpetratorName}
                  value={data.PerpetratorName["@value"]}
                  onChange={handleChange}
                  identical={compare("PerpetratorName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  className="ms-5"
                />
                <Input
                  name="VictimName"
                  label="If the child was a perpetrator, name the victim:"
                  error={errors.VictimName}
                  value={data.VictimName["@value"]}
                  onChange={handleChange}
                  identical={compare("VictimName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  className="ms-5"
                />
                <Input
                  name="RiskOfHarm"
                  label="History of risk of harm to self or others:"
                  error={errors.RiskOfHarm}
                  value={data.RiskOfHarm["@value"]}
                  onChange={handleChange}
                  identical={compare("RiskOfHarm")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="SubstanceAbuse"
                  label="History of alcohol or substance abuse:"
                  error={errors.SubstanceAbuse}
                  value={data.SubstanceAbuse["@value"]}
                  onChange={handleChange}
                  identical={compare("SubstanceAbuse")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="LegalInvolvement"
                  label="History of legal involvement:"
                  error={errors.LegalInvolvement}
                  value={data.LegalInvolvement["@value"]}
                  onChange={handleChange}
                  identical={compare("LegalInvolvement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Capacity for Developmental Functioning</div>
              <div className="container">
                <Input
                  name="FunctioningLevel"
                  label="Functioning Level:"
                  error={errors.FunctioningLevel}
                  value={data.FunctioningLevel["@value"]}
                  onChange={handleChange}
                  identical={compare("FunctioningLevel")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Strengths"
                  label="Strengths:"
                  error={errors.Strengths}
                  value={data.Strengths["@value"]}
                  onChange={handleChange}
                  identical={compare("Strengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Challenges"
                  label="Challenges:"
                  error={errors.Challenges}
                  value={data.Challenges["@value"]}
                  onChange={handleChange}
                  identical={compare("Challenges")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="SpecialNeeds"
                  label="Special Development needs, if applicable:"
                  error={errors.SpecialNeeds}
                  value={data.SpecialNeeds["@value"]}
                  onChange={handleChange}
                  identical={compare("SpecialNeeds")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="IQScore"
                  label="IQ:"
                  error={errors.IQScore}
                  value={data.IQScore["@value"]}
                  onChange={handleChange}
                  identical={compare("IQScore")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="PhysicalHealth"
                  label="Physical health:"
                  error={errors.PhysicalHealth}
                  value={data.PhysicalHealth["@value"]}
                  onChange={handleChange}
                  identical={compare("PhysicalHealth")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Hygiene"
                  label="Personal hygiene:"
                  error={errors.Hygiene}
                  value={data.Hygiene["@value"]}
                  onChange={handleChange}
                  identical={compare("Hygiene")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="SocialFunctioning"
                  label="Social functioning:"
                  error={errors.SocialFunctioning}
                  value={data.SocialFunctioning["@value"]}
                  onChange={handleChange}
                  identical={compare("SocialFunctioning")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="SchoolFunctioning"
                  label="School functioning, include IEP, if applicable:"
                  error={errors.SchoolFunctioning}
                  value={data.SchoolFunctioning["@value"]}
                  onChange={handleChange}
                  identical={compare("SchoolFunctioning")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="CommunicationFunctioning"
                  label="Language, speech and hearing functioning:"
                  error={errors.CommunicationFunctioning}
                  value={data.CommunicationFunctioning["@value"]}
                  onChange={handleChange}
                  identical={compare("CommunicationFunctioning")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="ADLSkills"
                  label="ADL Skills:"
                  error={errors.ADLSkills}
                  value={data.ADLSkills["@value"]}
                  onChange={handleChange}
                  identical={compare("ADLSkills")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="ILSkills"
                  label="Independent Living Skills abilities:"
                  error={errors.ILSkills}
                  value={data.ILSkills["@value"]}
                  onChange={handleChange}
                  identical={compare("ILSkills")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Clinical Overview</div>
              <div className="container">
                <Input
                  name="Diagnosis"
                  label="Diagnosis:"
                  error={errors.Diagnosis}
                  value={data.Diagnosis["@value"]}
                  onChange={handleChange}
                  identical={compare("Diagnosis")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="DeferredDiagnosis"
                  label="Deferred Diagnosis:"
                  error={errors.DeferredDiagnosis}
                  value={data.DeferredDiagnosis["@value"]}
                  onChange={handleChange}
                  identical={compare("DeferredDiagnosis")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Goals"
                  label="Identified goals:"
                  error={errors.Goals}
                  value={data.Goals["@value"]}
                  onChange={handleChange}
                  identical={compare("Goals")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Progress"
                  label="Progress on goals:"
                  error={errors.Progress}
                  value={data.Progress["@value"]}
                  onChange={handleChange}
                  identical={compare("Progress")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="PastMedications"
                  label="Past medications, including rationale for child's use:"
                  error={errors.PastMedications}
                  value={data.PastMedications["@value"]}
                  onChange={handleChange}
                  identical={compare("PastMedications")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Clinical Course of Treatment</div>
              <div className="container">
                <Input
                  name="PracticesUsed"
                  label="Evidenced-based practices used:"
                  error={errors.PracticesUsed}
                  value={data.PracticesUsed["@value"]}
                  onChange={handleChange}
                  identical={compare("PracticesUsed")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="EffectiveInterventions"
                  label="Effective Interventions:"
                  error={errors.EffectiveInterventions}
                  value={data.EffectiveInterventions["@value"]}
                  onChange={handleChange}
                  identical={compare("EffectiveInterventions")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Barriers"
                  label="Barriers to treatment:"
                  error={errors.Barriers}
                  value={data.Barriers["@value"]}
                  onChange={handleChange}
                  identical={compare("Barriers")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Condition upon Discharge</div>
              <div className="container">
                <Input
                  name="Prognosis"
                  label="Prognosis:"
                  error={errors.Prognosis}
                  value={data.Prognosis["@value"]}
                  onChange={handleChange}
                  identical={compare("Prognosis")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="DischargeLevelOfCare"
                  label="CRP level of care upon discharge:"
                  error={errors.DischargeLevelOfCare}
                  value={data.DischargeLevelOfCare["@value"]}
                  onChange={handleChange}
                  identical={compare("DischargeLevelOfCare")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="CurrentMedications"
                  label="Current medications, including rationale for child's use:"
                  error={errors.CurrentMedications}
                  value={data.CurrentMedications["@value"]}
                  onChange={handleChange}
                  identical={compare("CurrentMedications")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Discharge Placement</div>
              <div className="container">
                <Input
                  name="DischargedTo"
                  label="Person or agency where the child was discharged:"
                  error={errors.DischargedTo}
                  value={data.DischargedTo["@value"]}
                  onChange={handleChange}
                  identical={compare("DischargedTo")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="MeetPermanency"
                  label="Is the discharge placement meeting the permanency plan? If not, why?"
                  error={errors.MeetPermanency}
                  value={data.MeetPermanency["@value"]}
                  onChange={handleChange}
                  identical={compare("MeetPermanency")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">After Care Plan/Recommendations</div>
              <div className="container">
                <Checkbox
                  name="AfterCarePlanNA"
                  label="No Person/Agency responsible for after care plans/recommendations"
                  checked={data.AfterCarePlanNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.AfterCarePlanNA["@value"] !== "true" ? (
                  <FieldTable
                    name="AfterCarePlanDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col-4 border-end py-2">
                          Person/Agency
                        </div>
                        <div className="col py-2">
                          Description of the role/task/responsibilities
                        </div>
                      </div>
                      {data.AfterCarePlanDetails["@value"] &&
                        JSON.parse(data.AfterCarePlanDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-4 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          setData,
                                          index,
                                          "AfterCarePlanDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "AfterCarePlanDetails"
                                        );
                                      }}
                                    ></i>
                                    <Select
                                      name="Agency"
                                      value={item.Agency}
                                      options={agencyOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "AfterCarePlanDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Agency === "Other" ? (
                                      <>
                                        <br />
                                        <Input
                                          name="AgencyOther"
                                          value={item.AgencyOther}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "AfterCarePlanDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    {!formDisabled && (
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "AfterCarePlanDetails"
                                          );
                                        }}
                                      ></i>
                                    )}
                                    {item.Agency === "Other"
                                      ? item.AgencyOther
                                      : item.Agency}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "AfterCarePlanDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("AfterCarePlanDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Person/Agency
                      </button>
                    ) : null}
                    {errors.AfterCarePlanDetails && (
                      <div className="alert alert-danger">
                        {errors.AfterCarePlanDetails}
                      </div>
                    )}
                  </FieldTable>
                ) : null}
              </div>
              <div className="container">
                <div className="h5">
                  Next medical exams, include provider and date/time
                </div>
                <Input
                  name="PhysicalExam"
                  label="Physical:"
                  error={errors.PhysicalExam}
                  value={data.PhysicalExam["@value"]}
                  onChange={handleChange}
                  identical={compare("PhysicalExam")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="DentalExam"
                  label="Dental:"
                  error={errors.DentalExam}
                  value={data.DentalExam["@value"]}
                  onChange={handleChange}
                  identical={compare("DentalExam")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="VisionExam"
                  label="Vision:"
                  error={errors.VisionExam}
                  value={data.VisionExam["@value"]}
                  onChange={handleChange}
                  identical={compare("VisionExam")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="OtherExam"
                  label="Other:"
                  error={errors.OtherExam}
                  value={data.OtherExam["@value"]}
                  onChange={handleChange}
                  identical={compare("OtherExam")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <div className="h5">Follow up Appointments/Meetings</div>
              <div className="container">
                <Checkbox
                  name="ScheduledAppointments"
                  label="Scheduled appointments for the child/family"
                  checked={data.ScheduledAppointments["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.ScheduledAppointments["@value"] === "true" ? (
                  <Input
                    name="ScheduledAppointmentsDetails"
                    label="Provider, location, date and time:"
                    error={errors.ScheduledAppointmentsDetails}
                    value={data.ScheduledAppointmentsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("ScheduledAppointmentsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <Checkbox
                  name="ScheduledMeetings"
                  label="Scheduled meetings for the child/family"
                  checked={data.ScheduledMeetings["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.ScheduledMeetings["@value"] === "true" ? (
                  <Input
                    name="ScheduledMeetingsDetails"
                    label="Provider, location, date and time:"
                    error={errors.ScheduledMeetingsDetails}
                    value={data.ScheduledMeetingsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("ScheduledMeetingsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </div>
            </>
          ) : null}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYPCCPCPDischargeSummaryv2;
